import { Button, Card, CardContent, CardHeader, Stack } from '@mui/material';
import { FieldTextRequired } from '../../form';
import { useForm } from 'react-typed-form';
import { useTypedDispatch } from '../../reducers';
import { useApiCallable } from '../../api';
import { ApiError } from '../../api/sustain-api-request';
import { ErrorList } from '../../components';
import { useNavigate } from 'react-router';

type Props = {
  showTwoFactorForm: (phoneDigits: string) => void;
};

type LoginForm = {
  email: string;
  password: string;
};

export default function LoginForm({ showTwoFactorForm }: Props) {
  const dispatch = useTypedDispatch();
  const callApi = useApiCallable();
  const navigate = useNavigate();

  const form = useForm<LoginForm>({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async (values, { setLoading }) => {
      setLoading(true);
      const response = await callApi<
        {
          twoFactorRequired: boolean;
          sms2faMetadata: { phoneLastDigits: string } | null;
          user: {
            id: string;
            email: string;
            role?: string[];
            timezone?: string;
          } | null;
        },
        any
      >(
        '/login-check',
        {
          method: 'POST',
          jsonBody: values,
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 401) {
              form.addSubmitError('_form', error.response.data.error);
            }
            originalHandler(error);
          },
        },
      );

      setLoading(false);

      if (!response) return;

      console.log(response.data);

      const sessionId = (response.headers as any).get('sustain-session-id');

      if (!sessionId) {
        alert('No session id');
        return;
      }

      const { twoFactorRequired, sms2faMetadata, user } = response.data;

      if (twoFactorRequired) {
        showTwoFactorForm(sms2faMetadata?.phoneLastDigits ?? '');
        dispatch({
          type: 'LOGIN_AWAITING_2FA',
          payload: {
            sessionId,
          },
        });
      } else if (user) {
        dispatch({
          type: 'LOGIN',
          payload: {
            user: {
              id: user.id,
              email: user.email,
              sessionId,
              roles: user.role ?? [],
              timezone: user.timezone ?? '',
            },
          },
        });
      }
    },
  });

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        form.handleSubmit();
      }}
    >
      <Card>
        <CardHeader title="Log in" />
        <CardContent>
          <Stack spacing={2}>
            <FieldTextRequired label="Email" field={form.getField('email')} />
            <FieldTextRequired
              label="Password"
              field={form.getField('password')}
              type="password"
            />
            <Button variant="contained" type="submit" disabled={form.isLoading}>
              Log in
            </Button>
            <Button
              variant="text"
              onClick={() => navigate('/forgot-password')}
              disabled={form.isLoading}
            >
              Forgot password?
            </Button>
            <ErrorList errors={form.lastErrors._form} />
          </Stack>
        </CardContent>
      </Card>
    </form>
  );
}
